import React, { FC, useEffect, useState } from 'react'
import firebase from 'firebase/app'
import * as Sentry from '@sentry/react'
import { useApolloClient } from '@apollo/client'
import { DownloadIcon, XIcon } from '@heroicons/react/outline'
import { Spinner, Button, Dialog } from '../../System'
import { searchStorySets } from '../../../graphql/StorySet/searchStorySets'
import { useNotifications } from '../../Notifications'
import { StyledStorySetModal } from '.'
import { IssueModalGallery } from '../../IssueView/IssueModal'
import RenderSlate from '../../Slate/RenderSlate'
import { FlexGroup } from '../../Grid'
import { ArchiveFindOneQuery } from '../../../graphql/Archive/__generated__/ArchiveFindOneQuery'
import { archiveFindOneQuery } from '../../../graphql/Archive/archiveFindOne'
import {
  SearchStorySets,
  SearchStorySets_searchStorySets_docs
} from '../../../graphql/StorySet/__generated__/SearchStorySets'

type Props = {
  _id: string;
  selectedStorySet?: SearchStorySets_searchStorySets_docs;
  isOpen?: boolean;
  onClose: () => void;
}

const StorySetModal: FC<Props> = ({
  _id, selectedStorySet, isOpen, onClose
}) => {
  const apollo = useApolloClient()
  const storage = firebase.storage()
  const { addNotification } = useNotifications()
  const [downloadUrl, setDownloadUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [storySet, setStorySet] = useState<SearchStorySets_searchStorySets_docs>()

  const loadStorySetInfo = async () => {
    try {
      setLoading(true)

      const { data } = await apollo.query<SearchStorySets>({
        fetchPolicy: 'no-cache',
        query: searchStorySets,
        variables: { storySet: _id, page: 0, items: 1 }
      })

      setStorySet(data.searchStorySets?.docs[0])
    }
    catch (err) {
      addNotification({ message: err.message, type: 'error', interval: 10000 })
      Sentry.captureException(err)
    }
    finally {
      setLoading(false)
    }
  }

  const getUrl = async () => {
    const { data } = await apollo.query<ArchiveFindOneQuery>({
      query: archiveFindOneQuery,
      variables: { recordId: _id, type: 'storyset' }
    })

    const zipPath = data.archiveFindOne?.fullPath

    if (zipPath) {
      const fileRef = storage.ref().child(zipPath)
      const zipUrl = await fileRef.getDownloadURL()
      return setDownloadUrl(zipUrl)
    }

    setDownloadUrl('')
  }

  useEffect(() => {
    if (!_id) {
      return
    }

    getUrl()

    if (selectedStorySet) {
      return setStorySet(selectedStorySet)
    }

    loadStorySetInfo()
  }, [selectedStorySet, _id])

  return (
    <Dialog isOpen={isOpen} onClose={() => onClose()}>
      <StyledStorySetModal>
        {!loading && storySet && (
          <>
            <div className="modal-heading">
              <div>
                <span className="modal-title">{storySet.title}</span>
              </div>
              <FlexGroup>
                {downloadUrl && (
                  <Button
                    theme="ghost"
                    className="modal-set-download-button"
                    iconPos="right"
                    icon={<DownloadIcon />}
                    externalURL={downloadUrl}
                    ariaLabel="Download templates"
                  >
                    <span>Download Templates</span>
                  </Button>
                )}
                <Button
                  className="close-modal-button"
                  theme="ghost"
                  type="large"
                  isIcon
                  onClick={() => onClose()}
                >
                  <XIcon />
                </Button>
              </FlexGroup>
            </div>
            <div className="modal-grid">
              <div className="modal-grid-item">
                <IssueModalGallery
                  setFullScreen={setIsFullScreen}
                  isFullScreen={isFullScreen}
                  modalType="story"
                  data={{
                    ...storySet,
                    records: storySet.stories || []
                  }}
                  canvaLink={storySet?.canvaLink}
                />
              </div>
              <div className="modal-grid-item">
                <div className="grid-box">
                  <div className="grid-box-header">
                    <span className="grid-box-header-title">Description</span>
                  </div>
                  <div className="grid-box-content">
                    <RenderSlate html={storySet.description} />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {loading && <Spinner isBlock />}
      </StyledStorySetModal>
    </Dialog>
  )
}

export default StorySetModal
